import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import RegistrationSuccessPage from './RegistrationSuccessPage';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import patentLogo from '../../assets/patent-logo.jpg';
import logoImage from '../../assets/images/LifeAssist-2.png';
import toast from 'react-hot-toast';

const { FiMic, FiAlertCircle, FiMail } = FiIcons;

const AuthPage = ({ initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const location = useLocation();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registrationSuccessEmail, setRegistrationSuccessEmail] = useState('');

  useEffect(() => {
    // Check for error message from navigation state (from invalid invite redirects)
    if (location.state?.error) {
      setErrorMessage(location.state.error);
      setShowError(true);
      toast.error(location.state.error);
      
      // Clear the error from navigation state to prevent it from persisting
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleShowEmailConfirmation = (email) => {
    setConfirmationEmail(email);
    setShowEmailConfirmation(true);
    setIsLogin(true); // Switch to login mode
  };

  const handleShowRegistrationSuccess = (email) => {
    setRegistrationSuccessEmail(email);
    setShowRegistrationSuccess(true);
  };

  // Show registration success page if registration was just completed
  if (showRegistrationSuccess) {
    return <RegistrationSuccessPage email={registrationSuccessEmail} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 mt-4 pt-safe">
      <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {/* Left side - Branding */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-center lg:text-left"
        >
          <div className="flex items-center justify-center lg:justify-start mb-8">
            <img src={logoImage} alt="Life Assist / Life Coach Assist" className="w-full max-w-sm h-auto" />
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Life Assist / Life Coach Assist <span className="text-xl">Pat. Pend.</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8">
            Your always-on connection to your coach or consultant, ready whenever you are.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span className="text-gray-700">Available 24/7 to support your growth journey.</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span className="text-gray-700">Captures your celebrations, challenges, regrets, and action plans.</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span className="text-gray-700">Provides guidance when your coach or consultant is unavailable.</span>
            </div>
          </div>

          <div className="mt-4">
            <img src={patentLogo} className='w-32' alt="Patent Pending" />
          </div>
        </motion.div>

        {/* Right side - Auth Form */}
        <div>
          {/* Email Confirmation Alert */}
          {showEmailConfirmation && confirmationEmail && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4"
            >
              <div className="flex items-start">
                <SafeIcon
                  icon={FiMail}
                  className="text-green-600 mt-0.5 mr-3 flex-shrink-0"
                />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900 mb-1">
                    Check Your Email
                  </h4>
                  <div className="text-sm text-green-700">
                    <p className="mb-2">
                      We've sent a confirmation email to <span className="font-semibold">{confirmationEmail}</span>
                    </p>
                    <p className="mb-2">
                      Please check your email and click the confirmation link to activate your account.
                    </p>
                    <p className="text-xs">
                      Once confirmed, you can sign in using the form below.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEmailConfirmation(false)}
                  className="text-green-400 hover:text-green-600 ml-2"
                >
                  <span className="sr-only">Dismiss</span>
                  ×
                </button>
              </div>
            </motion.div>
          )}

          {/* Error Alert */}
          {showError && errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
            >
              <div className="flex items-start">
                <SafeIcon
                  icon={FiAlertCircle}
                  className="text-red-600 mt-0.5 mr-3 flex-shrink-0"
                />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900 mb-1">
                    Access Denied
                  </h4>
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowError(false)}
                  className="text-red-400 hover:text-red-600 ml-2"
                >
                  <span className="sr-only">Dismiss</span>
                  ×
                </button>
              </div>
            </motion.div>
          )}

          {isLogin ? (
            <LoginForm onToggleMode={() => setIsLogin(false)} />
          ) : (
            <RegisterForm
              onToggleMode={() => setIsLogin(true)}
              onShowEmailConfirmation={handleShowEmailConfirmation}
              onShowRegistrationSuccess={handleShowRegistrationSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;